<template>
    <div>
        <psi-content-loader v-if="loading"> </psi-content-loader>
        <div v-else>
            <psi-page-title
                key="page-title"
                :title="page.title"
                :icon="page.icon"
                :breadcrumbs="breadcrumbs"
            >
            </psi-page-title>
            <v-card class="mt-4 px-3 py-6" :loading="dataLoading">
                <v-row class="justify-center align-stretch fill-height">
                    <v-col cols="7">
                        <template v-for="(item, index) in items">
                            <div :id="item.id" :key="index">
                                <a :name="item.id"></a>
                                <component
                                    :ref="item.id"
                                    :key="`${item.component}-${key}`"
                                    :section-loading="sectionLoading"
                                    :is="item.component"
                                    :id="item.id"
                                    :program="program"
                                    :sessions="sessions"
                                    :instructors="instructors"
                                    :query="query"
                                    @update="updateSection"
                                    @remove="removeSection"
                                    @mounted="componentMounted"
                                ></component>
                            </div>
                        </template>
                    </v-col>
                    <v-col cols="2">
                        <psi-scroll-nav
                            :items="items"
                            v-if="items.length > 0"
                        ></psi-scroll-nav>
                    </v-col>
                </v-row>
            </v-card>
        </div>
    </div>
</template>

<script>
import HomeLink from "@/mixins/homelink";
import ProgramIcons from "@/components/program/mixins/programIcons";
import utils from "@/components/program/library/programUtils";
import omit from "lodash.omit";

import { mapGetters, mapActions, mapMutations } from "vuex";
export default {
    name: "program-edit",
    mixins: [HomeLink, ProgramIcons],
    components: {
        ProgramDetails: () =>
            import(
                "@/components/program/components/ProgramEdit/ProgramDetails.vue"
            ),
        ProgramAddress: () =>
            import(
                "@/components/program/components/ProgramEdit/ProgramAddress.vue"
            ),
        ProgramSessions: () =>
            import(
                "@/components/program/components/ProgramEdit/ProgramSessions.vue"
            ),
        ProgramInstructors: () =>
            import(
                "@/components/program/components/ProgramEdit/ProgramInstructors.vue"
            ),
        BillingContact: () =>
            import(
                "@/components/program/components/ProgramEdit/ProgramBillingContact.vue"
            ),

        ProgramOptionsMenu: () =>
            import("@components/program/components/ProgramOptionsMenu.vue"),
    },
    data() {
        return {
            query: {},
        };
    },
    watch: {
        $route: {
            immediate: true,
            deep: true,
            handler(to) {
                this.query = to.query;
                if (this.$_.get(to, "name", "") === "Program Edit") {
                    if (
                        this.$_.has(to, "params.id") &&
                        !isNaN(this.$_.get(to, "params.id"))
                    ) {
                        this.getCurrentProgram(to.params.id);
                    } else {
                        this.SET_CURRENT_PROGRAM({
                            program: utils.init(this.randomIcon),
                        });
                    }
                }
            },
        },
    },
    computed: {
        ...mapGetters("Programs", [
            "loading",
            "dataLoading",
            "sectionLoading",
            "currentProgram",
        ]),
        program() {
            return this.$_.get(this.currentProgram, "program", {});
        },
        sessions() {
            return this.$_.get(this.currentProgram, "sessions", []);
        },
        instructors() {
            return this.$_.get(this.currentProgram, "instructors", []);
        },
        key() {
            return this.sessions.length + this.instructors.length;
        },
        page() {
            return {
                title:
                    this.$_.get(this.currentProgram, "program.name", "")
                        .length > 0
                        ? this.currentProgram.program.name
                        : "Add Program",
                icon: this.$config("icons.program"),
            };
        },
        breadcrumbs() {
            return [
                Object.assign(
                    {
                        text: "Home",
                        disabled: false,
                    },
                    this.homeUrl
                ),
                {
                    text: "Programs",
                    disabled: false,
                    to: {
                        name: "Program Dashboard",
                    },
                },
                {
                    text:
                        Object.keys(this.currentProgram).length > 0
                            ? this.currentProgram.program.name.length > 0
                                ? this.currentProgram.program.name
                                : "Add Program"
                            : "Edit Program",
                    disabled: true,
                    to: "#",
                },
            ];
        },
        items() {
            return [
                {
                    id: "program-details",
                    component: "program-details",
                    title: "Program Details",
                    icon: this.$config("icons.program"),
                },
                {
                    id: "program-address",
                    component: "program-address",
                    title: "Program Address",
                    icon: this.$config("icons.address"),
                },
                {
                    id: "billing-contact",
                    component: "billing-contact",
                    title: "Billing Contact",
                    icon: this.$config("icons.billing"),
                },
                {
                    id: "program-instructors",
                    component: "program-instructors",
                    title: "Program Instructors",
                    icon: this.$config("icons.instructor"),
                },
                {
                    id: "program-sessions",
                    component: "program-sessions",
                    title: "Program Sessions",
                    icon: this.$config("icons.session"),
                },
            ];
        },
    },
    methods: {
        ...mapMutations("Programs", ["SET_CURRENT_PROGRAM"]),
        ...mapActions("Programs", [
            "getCurrentProgram",
            "createProgram",
            "updateProgram",
            "updateProgramAddress",
            "updateProgramContact",
            "createInstructors",
            "updateInstructor",
            "inactivateInstructor",
            "createSession",
            "updateSession",
        ]),
        removeSection(id, data) {
            switch (id) {
                case "program-instructors":
                    this.inactivateInstructor({
                        id: data.id,
                        instructorId: data.instructorId,
                    });
                    break;
            }
        },
        updateSection(id, data) {
            switch (id) {
                case "program-details":
                    if (this.$_.has(data, "id")) {
                        this.updateProgram({
                            id: data.id,
                            data: omit(data, "id"),
                        });
                    } else {
                        this.createProgram(data);
                    }
                    break;
                case "program-address":
                    this.updateProgramAddress({
                        id: data.id,
                        data: omit(data, "id"),
                    });
                    break;
                case "billing-contact":
                    this.updateProgramContact({
                        id: data.id,
                        data: omit(data, "id"),
                    });
                    break;

                case "program-instructors":
                    this.$_.get(data, "data.meta.mode") === "Add"
                        ? this.createInstructors({
                              id: data.id,
                              data: omit(data.data, "id"),
                          })
                        : this.updateInstructor({
                              id: data.id, // program ID
                              data: omit(data.data, "meta"), // Instructor ID in data
                          });

                    break;
                case "program-sessions":
                    this.$_.get(data, "meta.mode") === "Add"
                        ? this.createSession({
                              id: data.program_id,
                              data: omit(data, ["program_id", "meta"]),
                          })
                        : this.updateSession({
                              id: data.program_id, // program ID
                              data: omit(data, "meta"), // Instructor ID in data
                          });
                    break;
                default:
                    console.log(id);
            }
        },
        scrollTo(id) {
            this.$vuetify.goTo(`#${id}`, {
                easing: "easeInOutCubic",
                offset: 15,
                duration: 450,
            });
        },
        componentMounted(id) {
            if (this.$_.get(this.query, "scrollTo", "") === id) {
                this.scrollTo(id);
            }
        },
    },
};
</script>

